import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Icon from "../../components/AppIcon";
import Image from "../../components/AppImage";
import FounderStory from "./components/FounderStory";
import TechnicalExpertise from "../homepage-cinematic-gateway/components/TechnicalExpertise";
import CreativeProcess from "./components/CreativeProcess";
import EquipmentShowcase from "./components/EquipmentShowcase";
import CareerTimeline from "./components/CareerTimeline";
import CertificationsSection from "./components/CertificationsSection";
import VisionManifesto from "./components/Vision";

const StudioUniverseAbout = () => {
  const [activeSection, setActiveSection] = useState("founder");
  const [scrollY, setScrollY] = useState(0);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [showQuickNav, setShowQuickNav] = useState(false);
  const [sectionProgress, setSectionProgress] = useState(0);

  const footerRef = useRef(null);

  const navigationSections = [
    { id: "founder", label: "Founder Story", icon: "User" },
    { id: "process", label: "Creative Process", icon: "Workflow" },
    { id: "equipment", label: "Equipment", icon: "Camera" },
    { id: "timeline", label: "Career Timeline", icon: "Clock" },
    { id: "certifications", label: "Certifications", icon: "Award" },
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY + window.innerHeight / 2;

      for (let section of navigationSections) {
        const el = document.getElementById(section.id);
        if (!el) continue;

        const top = el.offsetTop;
        const bottom = top + el.offsetHeight;

        if (scrollPos >= top && scrollPos < bottom) {
          setActiveSection(section.id);
          break;
        }
      }

      const activeEl = document.getElementById(activeSection);
      if (activeEl) {
        const rect = activeEl.getBoundingClientRect();
        const progress = Math.min(
          1,
          Math.max(0, (window.innerHeight - rect.top) / rect.height),
        );
        setSectionProgress(progress);
      }

      let shouldShow = window.scrollY > window.innerHeight * 0.6;

      if (footerRef.current) {
        const footerTop = footerRef.current.getBoundingClientRect().top;
        if (footerTop < window.innerHeight * 0.75) {
          shouldShow = false;
        }
      }

      setShowQuickNav(shouldShow);
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeSection]);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Hero Section with Parallax */}
      <section
        data-theme="dark"
        className="relative h-screen flex items-center justify-center overflow-hidden"
      >
        <div
          className="absolute inset-0 z-0"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
          }}
        >
          <Image
            src="https://images.unsplash.com/photo-1516035069371-29a1b244cc32?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80"
            alt="Cinematic Studio Setup"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="relative z-10 text-center text-white max-w-7xl mx-auto px-4">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 text-yellow-400">
            Agency Universe
          </h1>
          <p className="text-xl md:text-2xl mb-6 text-white/90">
            Browse the story, process, and expertise behind our agency that
            brings visions to life.
          </p>
          <p className="text-lg md:text-xl mb-16 text-white/70">
            We're not just another creative agency. We're the bold voice of a
            new generation—where artistic excellence collides with business
            strategy, and every creative decision is engineered for maximum
            impact.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => scrollToSection("founder")}
              className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg font-semibold cinematic-transition cta-glow"
            >
              Discover Our Story
            </button>
            <Link
              to="/booking-concierge-premium-consultation"
              className="border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-4 rounded-lg font-semibold cinematic-transition"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
          <Icon name="ChevronDown" size={32} strokeWidth={1.5} />
        </div>
      </section>

      {/* Sticky Navigation */}
      {/* <nav
  className={`sticky top-20 z-40 transition-all duration-300 ease-out
    
  `}
>
  <div className="bg-white/70 backdrop-blur-md border-b border-border shadow-subtle">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between py-4">
        
        <div className="hidden lg:flex space-x-4 overflow-x-auto scrollbar-hide">
          {navigationSections.map((section) => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all duration-200 ${
                activeSection === section.id
                  ? "bg-accent text-white shadow-glow"
                  : "text-text-primary hover:text-accent hover:bg-surface"
              }`}
            >
              <Icon name={section.icon} size={18} strokeWidth={2} />
              <span>{section.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  </div>
</nav> */}

      {isMobileNavOpen && (
        <div className="fixed inset-0 top-20 z-50 lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setIsMobileNavOpen(false)}
          />

          {/* Drawer */}
          <div className="absolute left-0 top-0 h-full w-72 bg-white/95 backdrop-blur-xl p-4 shadow-2xl animate-slideIn">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-base font-semibold text-accent">
                Browse About Our Agency
              </h3>
              <button
                onClick={() => setIsMobileNavOpen(false)}
                className="p-2 rounded-lg hover:bg-surface transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-1 max-h-[80vh] overflow-y-auto pr-2">
              {navigationSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => {
                    scrollToSection(section.id);
                    setIsMobileNavOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors ${
                    activeSection === section.id
                      ? "bg-accent text-white"
                      : "text-text-primary hover:bg-surface hover:text-accent"
                  }`}
                >
                  <Icon name={section.icon} size={16} />
                  <span className="text-sm font-medium">{section.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content Sections */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <section data-theme="light" id="founder" className="">
          <FounderStory />
        </section>

        <section data-theme="light" id="vision" className="py-2 sm:py-4">
          <VisionManifesto className="" />
        </section>

        <section data-theme="light" id="process" className="py-2 sm:py-4">
          <CreativeProcess />
        </section>

        {/* <section id="equipment" className="py-2 sm:py-12">
          <EquipmentShowcase />
        </section> */}

        <section data-theme="light" id="timeline" className="py-2 sm:py-4">
          <CareerTimeline />
        </section>

        <section data-theme="light" id="certifications" className="py-2 sm:py-4">
          <CertificationsSection />
        </section>
      </div>

      {/* Call to Action Section */}
      <section data-theme="dark" className="bg-black-red-oscillate text-white py-8">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl text-white font-bold mb-2 sm:mb-4">
            Ready to Create Something <span className="text-red-600">Extraordinary</span>?
          </h2>
          <p className="text-lg sm:text-xl mb-2 text-white/90">
            Let's discuss how our technical expertise and creative vision can
            elevate your brand's visual narrative.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/booking-concierge-premium-consultation"
              className="bg-accent hover:bg-accent-dark text-white px-8 py-2 rounded-lg font-semibold cinematic-transition cta-glow"
            >
              Schedule Consultation
            </Link>
            <Link
              to="/cinematic-showcase-portfolio"
              className="border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-2 rounded-lg font-semibold cinematic-transition"
            >
              View Portfolio
            </Link>
          </div>
        </div>
      </section>

      <button
        onClick={() => setIsMobileNavOpen(true)}
        className={`fixed bottom-6 left-6 z-30 lg:hidden group ${
          showQuickNav
            ? "opacity-100 translate-x-0"
            : "opacity-0 -translate-x-4 pointer-events-none"
        } transition-all duration-500 ease-in-out`}
      >
        {/* Progress Ring */}
        <svg
          className="absolute inset-0 w-14 h-14 -rotate-90"
          viewBox="0 0 36 36"
        >
          <path
            d="M18 2
         a 16 16 0 0 1 0 32
         a 16 16 0 0 1 0 -32"
            fill="none"
            stroke="rgba(255,255,255,0.2)"
            strokeWidth="3"
          />
          <path
            d="M18 2
         a 16 16 0 0 1 0 32
         a 16 16 0 0 1 0 -32"
            fill="none"
            stroke="white"
            strokeWidth="3"
            strokeDasharray={`${sectionProgress * 100}, 100`}
            strokeLinecap="round"
          />
        </svg>

        {/* Button */}
        <div
          className="relative w-14 h-14 bg-gradient-to-br from-red-600 to-red-500 text-white rounded-full shadow-2xl flex items-center justify-center
    group-active:animate-haptic hover:scale-105 active:scale-95 transition-transform"
        >
          <Icon name="Menu" size={20} />
        </div>
      </button>
    </div>
  );
};

export default StudioUniverseAbout;
